import '@adornis/adornis-components/a-swipe';
import '@adornis/chemistry/elements/components/x-grid';

import { AdornisRouter } from '@adornis/base/client/a-router';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-link';
import { Renderable } from '@adornis/chemistry/renderable';
import '@adornis/germanium/components/ge-button';
import { GEInput } from '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-loading-state';
import '@adornis/germanium/components/ge-section';
import { colors } from '@adornis/germanium/theme';
import { Ghost, GhostTag } from '@adornis/ghost/db/ghost';
import { config } from 'config';
import { NodeBBTopic } from 'db/nodebb-topics';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit-html/directives/ref.js';
import { customElement, state } from 'lit/decorators.js';
import { from, map } from 'rxjs';
import '../components/wtg-blog-post-preview';
import '../components/wtg-footer';
import '../components/wtg-forum-topic-preview';
import '../components/wtg-swipe';
import '../components/wtg-user-voice';

@customElement('wtg-home')
export class WTGHome extends ChemistryLitElement {
  size = new SizeController(this);

  @state() blogPosts = new DataLoadingController(this, () =>
    from(
      Ghost.getAllPosts(
        'createdAt',
        false,
        10,
        'published',
      )(`{_id id createdAt slug title featureImage published excerpt}`),
    ),
  );

  @state() private partners = new ObservableController(
    this,
    from(
      Ghost.getAllPosts()(`{_id id createdAt slug title featureImage published tags ${GhostTag.allFields} excerpt}`),
    ).pipe(map(posts => posts.filter(post => post.tags.find(tag => tag.name === 'partnerswtg')))),
  );

  @state() forumPosts = new DataLoadingController(this, () =>
    from(NodeBBTopic.getRecentNodeBBTopics()(NodeBBTopic.allFields)),
  );

  public render() {
    const width = this.size.width!;
    return html`
      <x-flex horizontal ${css({ width: '100%', height: '100vh', minHeight: '718px', position: 'relative' })}>
        ${this.headerText()}
        ${this.size.isMobile
          ? nothing
          : html`
              <x-flex
                end
                ${css({
                  width: width < 1360 ? (width < 350 ? '10%' : '20%') : '40%',
                  minWidth: width < 1360 ? (width < 350 ? '30px' : '95px') : '250px',
                  height: '100%',
                  position: 'absolute',
                  right: '0',
                  backgroundColor: this.colors.secondary,
                  borderBottomLeftRadius: '20%',
                  float: 'right',
                })}
              >
                ${this.creativeBackdrop()}
              </x-flex>
            `}
      </x-flex>

      <x-flex center crossaxis-center ${css({ backgroundColor: 'white' })}>
        <x-flex space="xs" ${css({ width: '100%' })}>
          ${[
            this.whyJoin(),
            this.sellingPoints(),
            this.blog(),
            this.welcomeBox(),
            this.forum(),
            this.benefits(),
            this.socialProof(),
          ]}
        </x-flex>
      </x-flex>

      <div ${css({ height: '220px' })}></div>

      <wtg-footer></wtg-footer>
    `;
  }

  private email = createRef<GEInput>();
  private headerText() {
    const width = this.size.width!;
    return html`
      <ge-section ${css({ width: '100%', height: '100%' })}>
        <x-flex center space="xl" ${css({ height: '100%' })}>
          <x-flex
            ${css({
              maxWidth: this.sizes.maxReadableWidth,
              minWidth: width < 1300 ? '250px' : '270px',
              paddingTop: this.size.isMobile ? '220px' : 'none',
            })}
            space="lg"
          >
            <ge-h1> Your Adventure in Germany starts here! </ge-h1>
            <ge-text> By the community, for the community. </ge-text>

            <x-flex ?horizontal=${!(width < 960)} space="md" ${css({ width: width < 960 ? '80%' : '100%' })}>
              <ge-input
                ${ref(this.email)}
                placeholder="yourname@universityemail.com"
                .postfix=${html`
                  <x-icon
                    type="material-outlined"
                    ${css({ width: '20px', height: '20px', fontFamily: 'Material Icons Outlined' })}
                  >
                    email
                  </x-icon>
                `}
              >
              </ge-input>
              <ge-button
                @click=${() =>
                  AdornisRouter.goTo(`/signup`, new URLSearchParams({ email: this.email.value.value.value || '' }))}
                ${css({ alignSelf: 'start', boxShadow: 'none', height: '100%' })}
              >
                register for free
              </ge-button>
            </x-flex>
          </x-flex>

          <x-flex horizontal crossaxis-center space="md" @click=${() => AdornisRouter.goTo('/')}>
            <ge-h3> Supported by </ge-h3>
            <x-flex
              center
              crossaxis-center
              ${css({
                padding: `20px 10px`,
                background: '#EAF3D6',
                borderRadius: this.sizes.borderRadius,
                boxShadow: '-1px 2px 9px 5px rgba(0,0,0,0.05)',
              })}
            >
              <x-image
                .value=${require('client/assets/logos/barmer.png')}
                ${css({ width: '80px', mixBlendMode: 'multiply' })}
                mode="width"
              ></x-image>
            </x-flex>
          </x-flex>

          <x-flex crossaxis-center>
            <img
              ${css({ width: '30px', cursor: 'pointer' })}
              @click=${() => this.dispatchEvent(new CustomEvent('scroll-down', { bubbles: true }))}
              src="${require('client/assets/arrow_down.svg')}"
            />
          </x-flex>
        </x-flex>
      </ge-section>
    `;
  }

  private whyJoin() {
    return html`
      <x-flex
        crossaxis-center
        ${css({
          borderRadius: '100px',
          background: `linear-gradient(to bottom, ${this.colors.accent_50}, transparent)`,
          paddingTop: `200px`,
          marginTop: '-40px',
          width: '100%',
        })}
      >
        <ge-section>
          <x-flex space="lg" crossaxis-center ${css({ paddingBottom: this.spacing.xl })}>
            <ge-h2 center> <b> Why should you join </b> our community? </ge-h2>
            <ge-text center ${css({ maxWidth: '600px' })}>
              Whether you are planning your adventure in Germany or already living there, our community helps you
              throughout the whole journey.
            </ge-text>
          </x-flex>
        </ge-section>
      </x-flex>
    `;
  }

  private sellingPoints() {
    const makeHeadingWithButton = (text: Renderable, buttonText: string, link: string) => html`
      <x-flex space="md" crossaxis-start ${css({ width: this.sizes.maxReadableWidth })}>
        <ge-h3> ${text} </ge-h3>
        <ge-button mode="outline" icon="east" iconend href=${link}> ${buttonText} </ge-button>
      </x-flex>
    `;
    return html`
      <x-flex space="xl" ${css({ position: 'relative' })}>
        <ge-section>
          <x-flex space="xxl" horizontal wrap-reverse crossaxis-center>
            ${makeHeadingWithButton(
              'Ask other international students or young professionals all your questions and connect with them.',
              'ask the community',
              config.get('NODEBB_DOMAIN'),
            )}

            <x-image .value=${require('client/assets/svgs_home/speaking.svg')} flex mode="width"></x-image>
          </x-flex>
        </ge-section>

        <ge-section>
          <x-flex space-between horizontal wrap crossaxis-center>
            ${this.size.width < 1500
              ? nothing
              : html`
                  <x-image
                    .value=${require('client/assets/svgs_home/Half_Circle_1.svg')}
                    ${css({
                      width: '270px',
                      position: 'absolute',
                      left: '-50px',
                      transform: 'rotate(180deg)',
                      top: '410px',
                    })}
                    mode="width"
                  ></x-image>
                `}

            <x-flex ${css({ paddingLeft: this.spacing.xl })}>
              <x-image .value=${require('client/assets/svgs_home/profiles.svg')} flex mode="width"></x-image>
            </x-flex>
            ${makeHeadingWithButton(
              "Your safety first, that's why all our members are verified profiles.",
              'sign up',
              '/signup',
            )}
          </x-flex>
        </ge-section>

        <x-flex
          ${css({
            position: 'relative',
          })}
        >
          <img
            ${css({
              width: '284px',
              right: '0',
              top: '-20px',
              display: this.size.isTablet ? 'none' : 'flex',

              position: 'absolute',
            })}
            src=${require('client/assets/Pattern_home.svg')}
          />

          <ge-section>
            <x-flex ${css({ paddingRight: this.spacing.lg })}>
              <x-flex space="xxl" horizontal wrap-reverse crossaxis-center>
                ${makeHeadingWithButton(
                  'Get all the important information about traveling, living, studying and working in Germany.',
                  'get info',
                  '/blog',
                )}
                <x-image .value=${require('client/assets/svgs_home/berlin.svg')} flex mode="width"></x-image>
              </x-flex>
            </x-flex>
          </ge-section>
        </x-flex>

        <ge-section>
          <x-flex space="xxl" horizontal wrap crossaxis-center>
            <x-flex ${css({ paddingLeft: this.spacing.lg })}>
              <x-image .value=${require('client/assets/svgs_home/conversation.svg')} flex mode="width"></x-image>
            </x-flex>
            ${makeHeadingWithButton(
              'Participate in workshops about relevant topics, company presentations as well as field trips to renowned cooperating companies.',
              'go to events',
              '/events',
            )}
          </x-flex>
        </ge-section>

        <x-flex
          ${css({
            position: 'relative',
          })}
        >
          <img
            ${css({
              width: '324px',
              right: '0',
              display: this.size.isTablet ? 'none' : 'flex',

              position: 'absolute',
            })}
            src=${require('client/assets/background_images/Half_circle_green_2.svg')}
          />

          <ge-section>
            <x-flex space="xxl" horizontal wrap-reverse crossaxis-center>
              ${makeHeadingWithButton('Have fun at our international welcome parties.', 'go to events', '/events')}

              <x-flex ${css({ paddingRight: this.spacing.lg })}>
                <x-image .value=${require('client/assets/svgs_home/party.svg')} flex mode="width"></x-image>
              </x-flex>
            </x-flex>
          </ge-section>
        </x-flex>

        <x-flex
          ${css({
            position: 'relative',
          })}
        >
          <img
            ${css({
              display: this.size.isTablet ? 'none' : 'flex',
              width: '244px',
              top: '100px',
              left: '0',
              pointerEvents: 'none',
              position: 'absolute',
            })}
            src=${require('client/assets/background_images/dots.svg')}
          />
          <ge-section>
            <x-flex space="xxl" horizontal wrap crossaxis-center>
              <x-image .value=${require('client/assets/svgs_home/welcome_box.svg')} flex mode="width"></x-image>
              ${makeHeadingWithButton(
                'Get a welcome box with nice giveaways once you have arrived in Germany.',
                'get your free welcome box now',
                '/welcome_box',
              )}
            </x-flex>
          </ge-section>
        </x-flex>

        <ge-section>
          <x-flex
            space="xxl"
            horizontal
            wrap-reverse
            crossaxis-center
            @click=${() => {
              const section = this.shadowRoot?.querySelector('#benefits_section');
              section?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            ${makeHeadingWithButton('Benefit from our partner services.', 'see benefits', '#')}
            <x-image .value=${require('client/assets/svgs_home/partners.svg')} flex mode="width"></x-image>
          </x-flex>
        </ge-section>
      </x-flex>
    `;
  }

  private blog() {
    return html`
      <x-flex space="lg" crossaxis-center>
        <ge-section ${css({ paddingBottom: this.spacing.lg, paddingTop: this.spacing.xxl })}>
          <ge-h2 center>
            <b> Everything you need to know about </b> <br />
            living, studying and working in Germany
          </ge-h2>
        </ge-section>

        ${this.blogPosts.isLoading
          ? html` <ge-loading-state></ge-loading-state> `
          : html`
              <!-- <a-swipe controls use-outer-height ${css({ height: '500px', width: '100%' })}>
                  ${this.blogPosts.value!.map(
                post => html`
                  <x-flex
                    ?horizontal=${!this.size.isMobile}
                    end
                    ${css({
                      background: `linear-gradient(${this.size.isMobile ? '195deg' : '105deg'}, transparent 30%, ${
                        this.colors.secondary
                      } 50%), url(${post.featureImage})`,
                      borderRadius: this.sizes.borderRadius,
                      color: this.colors.on.secondary,
                    })}
                  >
                    <x-flex
                      space="lg"
                      padding="xl"
                      center
                      ${css(this.size.isMobile ? { width: '100%', height: '50%' } : { width: '50%', height: '100%' })}
                    >
                      <ge-h3> ${post.title} </ge-h3>
                      <ge-text> ${post.excerpt} </ge-text>
                      <ge-button mode="text" icon="add_box" href="/blog/${post.slug}"> see more </ge-button>
                    </x-flex>
                  </x-flex>
                `,
              )}
                </a-swipe> -->
              <wtg-swipe
                .blogPosts=${this.blogPosts.value}
                ${css({
                  height: '500px',
                  width: '90%',
                })}
              >
              </wtg-swipe>
            `}

        <x-flex ${css({ paddingBottom: this.spacing.xl })}>
          <ge-button mode="outline" iconend icon="east" href="/blog" ${css({ alignSelf: 'center' })}>
            check out our blog
          </ge-button>
        </x-flex>
      </x-flex>
    `;
  }

  private welcomeBox() {
    return html`
      <x-flex ${css({ background: colors.yellow, padding: `${this.spacing.xxl} 0` })}>
        <ge-section>
          <x-flex space="xxl">
            <x-flex space="xl" horizontal wrap crossaxis-center>
              <x-image
                ${css({ flex: '5', transform: 'scale(1.2)' })}
                mode="width"
                .value=${require('client/assets/svgs_home/welcome_box_white.svg')}
              ></x-image>
              <x-flex space="xxl" ${css({ maxWidth: this.sizes.maxReadableWidth, flex: '4' })}>
                <x-flex space="lg">
                  <x-flex space="md">
                    <ge-h2> Welcome BOX </ge-h2>
                    <ge-h3> HOW IT WORKS? </ge-h3>
                  </x-flex>

                  <ge-text>
                    With the Welcome box, we want to make international students and young professionals in Germany very
                    happy as soon as they arrive in germany. Because, who doesn't love a welcome gift? The box contains
                    not only important information for their stay, but also free gifts and offers from leading brands in
                    Germany.
                  </ge-text>
                  <ge-text bold>
                    This is our way to say: Welcome to a new life, full of adventure, discovery, and friendships!
                  </ge-text>
                </x-flex>

                <ge-button href="/welcome_box" iconend icon="east" secondary>
                  get your free welcome box now!
                </ge-button>
              </x-flex>
            </x-flex>

            <ge-text center tone="subtle">
              *It is a limited shipment, take advantage of the opportunity to order them before they are gone.
            </ge-text>
          </x-flex>
        </ge-section>
      </x-flex>
    `;
  }

  private forum() {
    return html`
      <x-flex ${css({ background: colors.purple, padding: `${this.spacing.xxl} 0` })}>
        <ge-section ${css({ position: 'relative' })}>
          <img
            ${css({
              width: '144px',
              position: 'absolute',
              left: '30px',
              zIndex: '1',
            })}
            src=${require('client/assets/dots_living.svg')}
          />

          <x-flex
            space="xl"
            horizontal
            crossaxis-center
            ${css({
              zIndex: '2',
            })}
          >
            <x-flex space="lg" crossaxis-start ${css({ maxWidth: this.sizes.maxReadableWidth })}>
              <x-flex space="lg">
                <ge-h2 bold> Living in Germany! </ge-h2>
                <ge-h2> Everything about living and studying in Germany </ge-h2>
              </x-flex>

              <ge-button mode="outline" href=${config.get('NODEBB_DOMAIN')} iconend icon="east">
                ask the community
              </ge-button>
            </x-flex>
            <x-image flex mode="width" .value=${require('client/assets/svgs_home/forum_posts.svg')}></x-image>
          </x-flex>
        </ge-section>
      </x-flex>
    `;
  }

  private benefits() {
    return html`
      <x-flex space="lg" crossaxis-center ${css({ padding: `${this.spacing.xxl} 0px` })} id="benefits_section">
        <x-flex
          ${css({
            borderRadius: this.sizes.borderRadius,
            background: colors.blue,
            width: '90%',
            margin: 'auto',
            position: 'relative',
            padding: this.size.isTablet ? `${this.spacing.lg} ${this.spacing.md}` : this.spacing.xxl,
          })}
          crossaxis-center
          space="xl"
          padding=${this.size.isTablet ? 'md' : 'xxl'}
        >
          <ge-h2 center> <b> Benefit from </b> our partner services </ge-h2>

          <x-flex horizontal wrap space="md" center ${css({ width: '100%' })}>
            ${this.partners.value
              ?.slice(0, 3)
              .map(
                post => html`
                  <wtg-blog-post-preview
                    ${css({ maxWidth: '350px' })}
                    .postID=${post.id}
                    header=${post.title}
                    text=${post.excerpt}
                    .imageUrl=${post.featureImage}
                  ></wtg-blog-post-preview>
                `,
              )}
          </x-flex>

          <!-- creative stuff -->
          ${this.size.isTablet
            ? nothing
            : html`
                <x-image
                  .value=${require('client/assets/svgs_home/Half_Circle_1.svg')}
                  ${css({
                    width: '150px',
                    transform: 'rotate(180deg)',
                    position: 'absolute',
                    top: '50px',
                    left: '-40px',
                  })}
                  mode="width"
                ></x-image>
                <x-image
                  .value=${require('client/assets/svgs_home/Half_Circle_Blue.svg')}
                  ${css({
                    width: '75px',
                    position: 'absolute',
                    right: '-20px',
                    bottom: '40px',
                  })}
                  mode="width"
                ></x-image>
              `}
        </x-flex>

        <ge-button mode="outline" iconend icon="east" href="/benefits"> view more benefits </ge-button>
      </x-flex>
    `;
  }

  private socialProof() {
    return html`
      <ge-section>
        <x-flex space="xl" crossaxis-center>
          <ge-h2> <b> See what </b> our members say </ge-h2>
          <x-flex horizontal wrap space="xl">
            <wtg-user-voice
              image-url=${require('client/assets/svgs_home/user_voices/will.png')}
              occupation="Entrepreneur"
              name="Will, Australia"
              text="The ecosystem here has great support opportunities for expat founders - you just need to network with other international founders living in Germany and it starts to come together."
            ></wtg-user-voice>
            <wtg-user-voice
              image-url=${require('client/assets/svgs_home/user_voices/maria.png')}
              occupation="Young Professional"
              name="Maria, Colombia"
              text="I love the new people I have met, they have made my life much better."
            ></wtg-user-voice>
          </x-flex>
          <x-flex horizontal wrap end space="xl">
            <wtg-user-voice
              image-url=${require('client/assets/svgs_home/user_voices/pablo.png')}
              occupation="Young Professional"
              name="Pablo, Mexico"
              text="I love the new people I have met, they have made my life much better."
            ></wtg-user-voice>
            <wtg-user-voice
              image-url=${require('client/assets/svgs_home/user_voices/oksana.png')}
              occupation="Young Professional"
              name="Viviana, Kolumbien"
              text="I love the new people I have met, they have made my life much better."
            ></wtg-user-voice>
          </x-flex>
        </x-flex>
      </ge-section>
    `;
  }

  private creativeBackdrop() {
    const width = this.size.width!;
    return html`
      <x-flex
        ${css({
          width: width < 1360 ? '30%' : '100%',
          height: '90%',
          position: 'relative',
        })}
      >
        <img
          ${css({
            width: width < 1360 ? (width < 565 ? '80px' : '114px') : '144px',
            height: width < 1360 ? (width < 565 ? '38px' : '62px') : '72px',
            position: 'absolute',
            display: width < 1360 ? 'none' : 'flex',
            right: '86%',
          })}
          src=${require('client/assets/svgs_home/Half_Circle_1.svg')}
        />

        <img
          ${css({
            display: width < 1360 ? 'none' : 'flex',
            position: 'absolute',
            right: '20%',
            top: '130px',
            pointerEvents: 'none',
          })}
          width="128px"
          src=${require('client/assets/svgs_home/Dots.svg')}
        />

        <img
          width="208px"
          src="${require('client/assets/people_home/person2.png')}"
          ${css({
            position: 'absolute',
            top: '200px',
            right: '24%',
            display: width < 1360 ? 'none' : 'flex',
            borderRadius: '50%',
          })}
        />

        <img
          width="132px"
          ${css({
            borderRadius: '50%',
            position: 'absolute',
            right: '29%',
            display: width < 1360 ? 'none' : 'flex',
            bottom: '200px',
          })}
          src=${require('client/assets/people_home/person4.png')}
        />

        <img
          ${css({
            display: width < 1360 ? 'none' : 'flex',
            position: 'absolute',
            right: '0',
          })}
          width="80px"
          src=${require('client/assets/svgs_home/Half_Circle_2.svg')}
        />

        <img
          ${css({ display: width < 1360 ? 'none' : 'flex', position: 'absolute', bottom: '-40px', left: '35%' })}
          height="130px"
          src=${require('client/assets/blue_half_circle.svg')}
        />

        <img
          ${css({
            width: width < 1360 ? (width < 565 ? '48px' : '92px') : '304px',
            position: 'absolute',
            left: '10%',
            bottom: '200px',
          })}
          src=${require('client/assets/svgs_home/Pattern.svg')}
        />

        <img
          src=${require('client/assets/people_home/person3.png')}
          ${css({
            position: 'absolute',
            left: '12%',
            bottom: '380px',
            width: width < 1360 ? (width < 565 ? '80px' : '120px') : '126px',
            height: width < 1360 ? (width < 565 ? '80px' : '120px') : '126px',
            borderRadius: '50%',
          })}
        />

        <img
          src=${require('client/assets/people_home/person1.png')}
          ${css({
            position: 'absolute',
            top: '160px',
            left: '-50px',
            width: width < 1360 ? (width < 565 ? '50px' : '90px') : '132px',
            height: width < 1360 ? (width < 565 ? '50px' : '90px') : '132px',
            borderRadius: '50%',
          })}
        />
      </x-flex>
    `;
  }
}
